package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.flowyride.core.domain.analysis.Curvature
import com.flowyride.core.domain.analysis.RideCharacter
import com.flowyride.core.domain.analysis.SpeedBand
import com.flowyride.core.domain.analysis.distanceByBand
import com.flowyride.core.domain.analysis.speedStretches
import com.flowyride.feature.planner.PlannerAction
import com.flowyride.feature.planner.RouteUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.character_flowing
import com.flowyride.feature.planner.resources.character_pleasant
import com.flowyride.feature.planner.resources.character_twisty
import com.flowyride.feature.planner.resources.character_very_twisty
import com.flowyride.feature.planner.resources.curvature_value
import com.flowyride.feature.planner.resources.export_gpx_short
import com.flowyride.feature.planner.resources.favorite_save
import com.flowyride.feature.planner.resources.navigation_simulate
import com.flowyride.feature.planner.resources.navigation_start
import com.flowyride.feature.planner.resources.favorite_saved_label
import com.flowyride.feature.planner.resources.no_speed_camera_on_route
import com.flowyride.feature.planner.resources.speed_cameras_on_route
import com.flowyride.feature.planner.resources.speed_limits_fetch
import com.flowyride.feature.planner.resources.speed_limits_fetching
import com.flowyride.feature.planner.resources.retry
import com.flowyride.feature.planner.resources.route_engine_credit
import com.flowyride.feature.planner.resources.route_idle
import com.flowyride.feature.planner.resources.route_loading
import com.flowyride.feature.planner.resources.route_summary
import com.flowyride.feature.planner.ui.formatDistance
import com.flowyride.feature.planner.ui.formatDuration
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

/**
 * Always visible part of the sheet: what the route is ("135 km · 3 h 10", how winding) and the button to ride it,
 * or why there is none yet.
 */
@Composable
internal fun RouteStatusHeader(
    state: RouteUiState,
    onAction: (PlannerAction) -> Unit,
    canNavigate: Boolean,
    modifier: Modifier = Modifier,
) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        when (state) {
            RouteUiState.Idle -> Text(
                stringResource(Res.string.route_idle),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            RouteUiState.Loading -> Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                CircularProgressIndicator(Modifier.size(20.dp), strokeWidth = 2.dp)
                Text(stringResource(Res.string.route_loading), style = MaterialTheme.typography.bodyMedium)
            }

            is RouteUiState.Ready -> Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Column(Modifier.weight(1f)) {
                    Text(
                        stringResource(
                            Res.string.route_summary,
                            formatDistance(state.route.distanceMeters),
                            formatDuration(state.route.durationSeconds),
                        ),
                        style = MaterialTheme.typography.titleLarge,
                        maxLines = 1,
                    )
                    state.curvature?.let { CurvatureLine(it) }
                }
                // Riding needs the GPS: elsewhere (web, iOS for now) the ride can only be simulated.
                Button(onClick = { onAction(PlannerAction.StartNavigation(simulated = !canNavigate)) }) {
                    Text(stringResource(if (canNavigate) Res.string.navigation_start else Res.string.navigation_simulate))
                }
            }

            is RouteUiState.Failed -> {
                Text(stringResource(state.reason), style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.error)
                state.details?.let {
                    Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 2)
                }
                OutlinedButton(onClick = { onAction(PlannerAction.RetryRoute) }) {
                    Text(stringResource(Res.string.retry))
                }
            }
        }
    }
}

private const val UNKNOWN_SPEED_LEGEND_MIN_METERS = 500.0

/** Cameras, legs and where the route comes from: below the stops in the open sheet. */
@Composable
internal fun RouteDetails(state: RouteUiState.Ready, onAction: (PlannerAction) -> Unit, modifier: Modifier = Modifier) {
    val distanceByBand = remember(state.route) {
        // A few hundred metres without data (junctions): noise in the legend.
        state.route.speedStretches().distanceByBand()
            .filterNot { (band, meters) -> band == SpeedBand.UNKNOWN && meters < UNKNOWN_SPEED_LEGEND_MIN_METERS }
    }
    Column(modifier, verticalArrangement = Arrangement.spacedBy(6.dp)) {
        when {
            distanceByBand.isNotEmpty() -> SpeedLegend(distanceByBand)
            // A line without limits (imported, older favorite): they can be asked for.
            state.route.speedLimits.isEmpty() -> FetchSpeedLimitsButton(state.isFetchingSpeedLimits, onAction)
        }
        SpeedCamerasLine(state.speedCamerasOnRoute)
        if (state.route.legs.size >= 2 && state.route.legs.size == state.stops.size - 1) {
            LegList(state)
        }
        if (state.isImported) {
            ImportedBadge()
        } else {
            Text(
                stringResource(Res.string.route_engine_credit, state.engineName),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/** Save, export, and the simulation (the header's button already starts the real ride where there is one). */
@Composable
internal fun RouteActions(
    state: RouteUiState.Ready,
    onAction: (PlannerAction) -> Unit,
    onSaveFavorite: () -> Unit,
    canNavigate: Boolean,
    modifier: Modifier = Modifier,
) {
    Row(modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        OutlinedButton(onClick = onSaveFavorite, enabled = state.savedAs == null, modifier = Modifier.weight(1f)) {
            Text(stringResource(if (state.savedAs == null) Res.string.favorite_save else Res.string.favorite_saved_label), maxLines = 1)
        }
        OutlinedButton(onClick = { onAction(PlannerAction.ExportGpx) }, modifier = Modifier.weight(1f)) {
            Text(stringResource(Res.string.export_gpx_short), maxLines = 1)
        }
        if (canNavigate) {
            OutlinedButton(onClick = { onAction(PlannerAction.StartNavigation(simulated = true)) }, modifier = Modifier.weight(1f)) {
                Text(stringResource(Res.string.navigation_simulate), maxLines = 1)
            }
        }
    }
}

@Composable
private fun CurvatureLine(curvature: Curvature) {
    Text(
        stringResource(
            Res.string.curvature_value,
            stringResource(curvature.character.label),
            curvature.degreesPerKm.roundToInt(),
        ),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.primary,
    )
}

private val RideCharacter.label: StringResource
    get() = when (this) {
        RideCharacter.FLOWING -> Res.string.character_flowing
        RideCharacter.PLEASANT -> Res.string.character_pleasant
        RideCharacter.TWISTY -> Res.string.character_twisty
        RideCharacter.VERY_TWISTY -> Res.string.character_very_twisty
    }

/** One line per leg: "Annecy → Aix-les-Bains    45 km · 1 h 05". */
@Composable
private fun LegList(state: RouteUiState.Ready) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        state.route.legs.forEachIndexed { index, leg ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "${state.stops[index].place.name} → ${state.stops[index + 1].place.name}",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    stringResource(Res.string.route_summary, formatDistance(leg.distanceMeters), formatDuration(leg.durationSeconds)),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun FetchSpeedLimitsButton(fetching: Boolean, onAction: (PlannerAction) -> Unit) {
    if (fetching) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            CircularProgressIndicator(Modifier.size(16.dp), strokeWidth = 2.dp)
            Text(stringResource(Res.string.speed_limits_fetching), style = MaterialTheme.typography.bodySmall)
        }
    } else {
        OutlinedButton(onClick = { onAction(PlannerAction.FetchSpeedLimits) }) {
            Text(stringResource(Res.string.speed_limits_fetch))
        }
    }
}

@Composable
private fun SpeedCamerasLine(count: Int) {
    Text(
        if (count == 0) {
            stringResource(Res.string.no_speed_camera_on_route)
        } else {
            pluralStringResource(Res.plurals.speed_cameras_on_route, count, count)
        },
        style = MaterialTheme.typography.bodySmall,
        color = if (count == 0) MaterialTheme.colorScheme.onSurfaceVariant else MaterialTheme.colorScheme.error,
    )
}
